// ── Edge label helpers for PipelineStep (materialized view) ───────────────

export interface PipelineStep {
  id: number;
  systemId: number;
  systemAlias: string;
  step: string;
  serviceName: string;
  status: string;
  severity: string;
  message: string;
  eventType: string;
  sagaId: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
}

/** Extract short commit SHA from PipelineStep messages. */
export function getShortShaFromSteps(steps: PipelineStep[]): string | null {
  for (const s of steps) {
    const match = s.message?.match(/([a-f0-9]{7,8})\b/);
    if (match) return match[1];
  }
  return null;
}

/** Format duration from startedAt/completedAt timestamps. */
export function formatStepDuration(
  step: PipelineStep | undefined,
): string | null {
  if (!step?.startedAt || !step?.completedAt) return null;
  const ms =
    new Date(step.completedAt).getTime() - new Date(step.startedAt).getTime();
  if (ms <= 0) return null;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m${s}s` : `${m}m`;
}

/** Build a descriptive edge label for VCS→Build edges from pipelineState. */
export function stateVcsToBuildLabel(
  buildStep: PipelineStep | undefined,
  allBuildSteps: Map<string, PipelineStep>,
): string | undefined {
  if (!buildStep) return undefined;
  if (buildStep.status === "pending") return undefined;
  if (buildStep.status === "in_progress") return undefined;
  const parts: string[] = [];
  const sha = getShortShaFromSteps(Array.from(allBuildSteps.values()));
  if (sha) parts.push(sha);
  const dur = formatStepDuration(buildStep);
  if (dur) parts.push(dur);
  if (parts.length === 0) return buildStep.status === "completed" ? "✓" : "✗";
  return parts.join(" · ");
}

/** Build a descriptive edge label for Build→Deploy edges from pipelineState. */
export function stateBuildToDeployLabel(
  buildStep: PipelineStep | undefined,
  deployStep: PipelineStep | undefined,
): string | undefined {
  if (!deployStep || deployStep.status === "pending") return undefined;
  if (deployStep.status === "in_progress") return undefined;
  const dur = formatStepDuration(buildStep);
  if (deployStep.status === "failed") return dur ? `failed after ${dur}` : "✗";
  return dur ?? "✓";
}
